import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Users } from 'lucide-react'
import { Page } from '@/components/Page'
import { DataTable } from '@/components/DataTable'
import { Toolbar } from '@/components/Toolbar'
import { EmptyState } from '@/components/EmptyState'
import { FormField } from '@/components/FormField'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/auth/useAuth'
import { useCustomers } from '@/features/invoicing/queries'
import type { Customer } from '@/features/invoicing/types'

export function CustomersListPage() {
  const { hasPermission } = useAuth()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')

  const { data, isLoading } = useCustomers({ search: search.trim() || undefined })
  const canManage = hasPermission('invoicing.customers.manage')

  const columns = useMemo(() => [
    {
      accessorKey: 'display_name',
      header: 'Name',
      cell: ({ row }: { row: { original: Customer } }) => (
        <button
          type="button"
          onClick={() => navigate(`/invoicing/customers/${row.original.id}/edit`)}
          className="text-left font-medium text-primary hover:underline"
        >
          {row.original.display_name}
        </button>
      ),
    },
    { accessorKey: 'email', header: 'Email' },
    { accessorKey: 'phone', header: 'Phone' },
    {
      accessorKey: 'default_payment_terms_days',
      header: 'Terms',
      cell: ({ row }: { row: { original: Customer } }) => `${row.original.default_payment_terms_days} days`,
    },
    { accessorKey: 'default_currency', header: 'Currency' },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ row }: { row: { original: Customer } }) => (
        <Badge variant={row.original.is_active ? 'outline' : 'secondary'}>
          {row.original.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ], [navigate])

  return (
    <Page
      title="Customers"
      description="Manage your customer master list. Customers receive invoices and can be paid via the hosted pay page."
      actions={canManage ? (
        <Button onClick={() => navigate('/invoicing/customers/new')}>
          <Plus className="mr-2 size-4" /> New customer
        </Button>
      ) : undefined}
    >
      <Toolbar align="start">
        <FormField
          label=""
          placeholder="Search by name or email"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-64"
        />
      </Toolbar>
      <DataTable
        columns={columns as never}
        rows={data?.customers ?? []}
        loading={isLoading}
        empty={
          <EmptyState
            icon={Users}
            title="No customers yet"
            description={canManage ? 'Add your first customer so you can invoice them.' : 'No customers to display.'}
            action={canManage ? <Button onClick={() => navigate('/invoicing/customers/new')}><Plus className="mr-2 size-4" /> Add customer</Button> : null}
          />
        }
      />
    </Page>
  )
}
